import { useEffect, useState } from 'react';
import { THUMBNAIL_SIZE_DEFAULT } from '@/component/common/icons/AttachUtils';

/**
 * Pick the attachment thumbnail `size` preset responsively from the viewport width using
 * matchMedia (one lightweight listener; no ResizeObserver per item). The PC/MOBILE presets
 * for each context are managed centrally in THUMBNAIL_SIZE_CLIENT_CONTROL (AttachUtils).
 *
 * IMPORTANT: call this ONCE in a parent (e.g. the ticket-detail field lists or the list/modal)
 * and pass the result down to each AttachFileThumbnail via `size`. Calling it inside
 * AttachFileThumbnail would register one matchMedia listener per thumbnail.
 *
 * @param {{ PC: string, MOBILE: string }} [sizeControl] - a THUMBNAIL_SIZE_CLIENT_CONTROL entry.
 *   Defaults to THUMBNAIL_SIZE_DEFAULT. Returns `MOBILE` below the breakpoint, otherwise `PC`.
 * @returns {string}
 */
const MOBILE_QUERY = '(max-width: 480px)';

const getSize = (matches, sizeControl) => {
    const control = sizeControl || THUMBNAIL_SIZE_DEFAULT;
    return matches ? control.MOBILE : control.PC;
};

const useAttachThumbnailSize = (sizeControl) => {
    const [size, setSize] = useState(() => {
        if (typeof window === 'undefined' || !window.matchMedia) {
            return getSize(false, sizeControl);
        }
        return getSize(window.matchMedia(MOBILE_QUERY).matches, sizeControl);
    });

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return undefined;

        const mql = window.matchMedia(MOBILE_QUERY);
        const onChange = e => setSize(getSize(e.matches, sizeControl));

        // Sync once in case it changed between render and effect (or sizeControl changed).
        setSize(getSize(mql.matches, sizeControl));

        // addEventListener is the modern API; fall back to addListener for older browsers.
        if (mql.addEventListener) {
            mql.addEventListener('change', onChange);
            return () => mql.removeEventListener('change', onChange);
        }
        mql.addListener(onChange);
        return () => mql.removeListener(onChange);
    }, [sizeControl]);

    return size;
};

export default useAttachThumbnailSize;
